import { useState, useEffect, useMemo } from 'react';
import {
  X,
  Sparkles,
  Users,
  Receipt,
  SlidersHorizontal,
  ChevronRight
} from 'lucide-react';

/**
 * FriendBalanceChooserModal
 *
 * Mirrors Splitwise PaymentBalanceChooserFragment architecture.
 * Displays when settling up with a friend who shares one or more group debts.
 * Allows choosing between:
 *  1. Settle all balances (consolidated net total across all groups and 1:1 debts)
 *  2. Settle a specific group balance
 *  3. Settle non-group expenses
 *  4. More options (custom amount or manual account adjustment)
 *
 * @param {boolean} isOpen - Whether modal is visible
 * @param {function} onClose - Dismiss modal callback
 * @param {object} friend - Active friend details ({ user_id, display_name, nickname, avatar_url, email })
 * @param {object} balancesData - API response from GET /friends/:id/balances ({ overall, groups, non_group })
 * @param {string} initialCurrency - Preselected currency (default: 'AED')
 * @param {function} onSelectOption - Callback receiving chosen settlement parameters
 * @param {function} formatMoney - Optional money formatting function
 */
export default function FriendBalanceChooserModal({
  isOpen = false,
  onClose = () => {},
  friend = null,
  balancesData = null,
  initialCurrency = 'AED',
  onSelectOption = () => {},
  formatMoney = null
}) {
  // Format money helper
  const fmt = (amount, curr) => {
    if (typeof formatMoney === 'function') {
      return formatMoney(Math.abs(amount || 0), curr);
    }
    const val = Math.abs(parseFloat(amount || 0));
    return `${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${curr}`;
  };

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Extract all distinct currencies present in overall, groups, or non_group
  const currencies = useMemo(() => {
    const set = new Set();
    if (balancesData?.overall && Array.isArray(balancesData.overall)) {
      balancesData.overall.forEach(b => {
        if (b.currency) set.add(b.currency.toUpperCase());
      });
    }
    if (balancesData?.groups && Array.isArray(balancesData.groups)) {
      balancesData.groups.forEach(g => {
        if (g.currency) set.add(g.currency.toUpperCase());
      });
    }
    if (balancesData?.non_group && Array.isArray(balancesData.non_group)) {
      balancesData.non_group.forEach(ng => {
        if (ng.currency) set.add(ng.currency.toUpperCase());
      });
    }
    const list = Array.from(set);
    if (list.length === 0) {
      return [initialCurrency ? initialCurrency.toUpperCase() : 'AED'];
    }
    return list;
  }, [balancesData, initialCurrency]);

  const [activeCurrencyOverride, setActiveCurrencyOverride] = useState(null);

  const normalizedInitial = (initialCurrency || '').toUpperCase();
  const selectedCurrency = (activeCurrencyOverride && currencies.includes(activeCurrencyOverride))
    ? activeCurrencyOverride
    : (currencies.includes(normalizedInitial) ? normalizedInitial : (currencies[0] || 'AED'));

  if (!isOpen) return null;

  // Breakdown for selected currency
  const overallItem = balancesData?.overall?.find(
    b => (b.currency || '').toUpperCase() === selectedCurrency
  );
  const netAmount = parseFloat(overallItem?.amount || 0);

  const groupsInCurrency = (balancesData?.groups || []).filter(
    g => (g.currency || '').toUpperCase() === selectedCurrency && Math.abs(parseFloat(g.amount || 0)) > 0.0001
  );

  const nonGroupItem = balancesData?.non_group?.find(
    ng => (ng.currency || '').toUpperCase() === selectedCurrency && Math.abs(parseFloat(ng.amount || 0)) > 0.0001
  );
  const nonGroupAmount = nonGroupItem ? parseFloat(nonGroupItem.amount || 0) : 0;

  const friendName = friend?.nickname || friend?.display_name || friend?.email || 'Friend';
  const friendInitial = (friendName || 'F')[0].toUpperCase();

  const isNetOwedToMe = netAmount > 0.0001;
  const isNetIOwe = netAmount < -0.0001;

  return (
    <div
      className="modal-backdrop modal-backdrop-blur animate-fade-in"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        boxSizing: 'border-box'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="glass-panel modal-spring-enter modal-content-panel"
        style={{
          maxWidth: '480px',
          width: '100%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          background: 'var(--bg-card, #0f172a)',
          border: '1px solid var(--border-glass, rgba(255, 255, 255, 0.08))',
          borderRadius: '16px',
          padding: '20px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
          overflowY: 'auto',
          boxSizing: 'border-box'
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {friend?.avatar_url ? (
              <img
                src={friend.avatar_url}
                alt={friendName}
                style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '12px',
                  objectFit: 'cover',
                  border: '1px solid var(--border-glass, rgba(255, 255, 255, 0.1))'
                }}
              />
            ) : (
              <div
                style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(59, 130, 246, 0.2) 100%)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--accent-cyan, #00f5d4)',
                  fontWeight: '700',
                  fontSize: '16px'
                }}
              >
                {friendInitial}
              </div>
            )}
            <div>
              <h2 style={{ fontSize: '17px', fontWeight: '700', margin: 0, color: 'var(--text-main, #f8fafc)' }}>
                Choose a balance to settle
              </h2>
              <span style={{ fontSize: '12px', color: 'var(--text-muted, #94a3b8)' }}>
                With {friendName}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid var(--border-glass, rgba(255, 255, 255, 0.08))',
              color: 'var(--text-muted, #94a3b8)',
              borderRadius: '8px',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'background 0.15s ease'
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Currency Tabs (when balances exist across multiple currencies) */}
        {currencies.length > 1 && (
          <div
            style={{
              display: 'flex',
              gap: '8px',
              overflowX: 'auto',
              paddingBottom: '4px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.06)'
            }}
          >
            {currencies.map((curr) => {
              const isSelected = curr === selectedCurrency;
              return (
                <button
                  key={curr}
                  type="button"
                  onClick={() => setActiveCurrencyOverride(curr)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: isSelected ? '700' : '500',
                    cursor: 'pointer',
                    background: isSelected
                      ? 'rgba(16, 185, 129, 0.2)'
                      : 'rgba(255, 255, 255, 0.03)',
                    color: isSelected ? '#10b981' : 'var(--text-muted, #94a3b8)',
                    border: isSelected
                      ? '1px solid rgba(16, 185, 129, 0.4)'
                      : '1px solid rgba(255, 255, 255, 0.06)',
                    transition: 'all 0.15s ease',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {curr}
                </button>
              );
            })}
          </div>
        )}

        {/* Option 1: Settle all balances card */}
        <div
          role="button"
          tabIndex={0}
          onClick={() =>
            onSelectOption({
              type: 'settle_all',
              currency: selectedCurrency,
              amount: Math.abs(netAmount),
              direction: netAmount < 0 ? 'pay' : 'receive'
            })
          }
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelectOption({
                type: 'settle_all',
                currency: selectedCurrency,
                amount: Math.abs(netAmount),
                direction: netAmount < 0 ? 'pay' : 'receive'
              });
            }
          }}
          style={{
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(15, 23, 42, 0.8) 100%)',
            border: '1px solid rgba(16, 185, 129, 0.35)',
            borderRadius: '14px',
            padding: '16px',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            transition: 'transform 0.15s ease, border-color 0.15s ease',
            boxSizing: 'border-box'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '8px',
                  background: 'rgba(16, 185, 129, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#10b981'
                }}
              >
                <Sparkles size={16} />
              </div>
              <span style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-main, #f8fafc)' }}>
                Settle all balances
              </span>
            </div>
            <ChevronRight size={18} color="#10b981" />
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '8px' }}>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-muted, #94a3b8)', display: 'block' }}>
                Consolidates all group debts & direct expenses
              </span>
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  padding: '2px 8px',
                  borderRadius: '6px',
                  display: 'inline-block',
                  marginTop: '4px',
                  background: isNetOwedToMe
                    ? 'rgba(16, 185, 129, 0.15)'
                    : isNetIOwe
                    ? 'rgba(244, 63, 94, 0.15)'
                    : 'rgba(148, 163, 184, 0.15)',
                  color: isNetOwedToMe
                    ? '#10b981'
                    : isNetIOwe
                    ? '#f43f5e'
                    : 'var(--text-muted, #94a3b8)'
                }}
              >
                {isNetOwedToMe ? 'Owes you' : isNetIOwe ? 'You owe' : 'Settled up'}
              </span>
            </div>

            <div style={{ textAlign: 'right' }}>
              <span
                style={{
                  fontSize: '20px',
                  fontWeight: '800',
                  color: isNetOwedToMe
                    ? 'var(--color-inflow, #00f5d4)'
                    : isNetIOwe
                    ? 'var(--color-outflow, #ff4757)'
                    : 'var(--text-muted, #94a3b8)'
                }}
              >
                {isNetOwedToMe
                  ? `+${fmt(netAmount, selectedCurrency)}`
                  : isNetIOwe
                  ? `-${fmt(Math.abs(netAmount), selectedCurrency)}`
                  : `0.00 ${selectedCurrency}`}
              </span>
            </div>
          </div>
        </div>

        {/* Section: Or, settle a specific group balance */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
            <span
              style={{
                fontSize: '11px',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.6px',
                color: 'var(--text-muted, #94a3b8)'
              }}
            >
              Or, settle a specific group balance
            </span>
            <div style={{ flex: 1, height: '1px', background: 'rgba(255, 255, 255, 0.06)' }} />
          </div>

          {groupsInCurrency.length === 0 ? (
            <div
              style={{
                padding: '12px',
                borderRadius: '10px',
                background: 'rgba(255, 255, 255, 0.02)',
                border: '1px dashed rgba(255, 255, 255, 0.08)',
                textAlign: 'center',
                color: 'var(--text-muted, #94a3b8)',
                fontSize: '12px'
              }}
            >
              No active group balances in {selectedCurrency}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {groupsInCurrency.map((g) => {
                const gAmount = parseFloat(g.amount || 0);
                const gOwesMe = gAmount > 0;
                return (
                  <div
                    key={g.group_id}
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      onSelectOption({
                        type: 'group',
                        group_id: g.group_id,
                        group_name: g.group_name,
                        currency: g.currency,
                        amount: Math.abs(gAmount),
                        direction: gAmount < 0 ? 'pay' : 'receive'
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelectOption({
                          type: 'group',
                          group_id: g.group_id,
                          group_name: g.group_name,
                          currency: g.currency,
                          amount: Math.abs(gAmount),
                          direction: gAmount < 0 ? 'pay' : 'receive'
                        });
                      }
                    }}
                    style={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid rgba(255, 255, 255, 0.07)',
                      borderRadius: '12px',
                      padding: '12px 14px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      transition: 'background 0.15s ease, border-color 0.15s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                      {g.avatar_url ? (
                        <img
                          src={g.avatar_url}
                          alt={g.group_name}
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            objectFit: 'cover',
                            flexShrink: 0
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            background: 'rgba(99, 102, 241, 0.15)',
                            border: '1px solid rgba(99, 102, 241, 0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#818cf8',
                            flexShrink: 0
                          }}
                        >
                          <Users size={16} />
                        </div>
                      )}
                      <div style={{ minWidth: 0 }}>
                        <span
                          style={{
                            fontSize: '13px',
                            fontWeight: '600',
                            color: 'var(--text-main, #f8fafc)',
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {g.group_name}
                        </span>
                        <span
                          style={{
                            fontSize: '10.5px',
                            fontWeight: '600',
                            color: gOwesMe ? '#10b981' : '#f43f5e'
                          }}
                        >
                          {gOwesMe ? 'Owes you' : 'You owe'}
                        </span>
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                      <span
                        style={{
                          fontSize: '14px',
                          fontWeight: '700',
                          color: gOwesMe ? 'var(--color-inflow, #00f5d4)' : 'var(--color-outflow, #ff4757)'
                        }}
                      >
                        {gOwesMe ? `+${fmt(gAmount, g.currency)}` : `-${fmt(Math.abs(gAmount), g.currency)}`}
                      </span>
                      <ChevronRight size={16} color="var(--text-muted, #94a3b8)" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Direct Non-group Expenses (if present) */}
        {Math.abs(nonGroupAmount) > 0.0001 && (
          <div
            role="button"
            tabIndex={0}
            onClick={() =>
              onSelectOption({
                type: 'non_group',
                currency: selectedCurrency,
                amount: Math.abs(nonGroupAmount),
                direction: nonGroupAmount < 0 ? 'pay' : 'receive'
              })
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelectOption({
                  type: 'non_group',
                  currency: selectedCurrency,
                  amount: Math.abs(nonGroupAmount),
                  direction: nonGroupAmount < 0 ? 'pay' : 'receive'
                });
              }
            }}
            style={{
              background: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.07)',
              borderRadius: '12px',
              padding: '12px 14px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer',
              transition: 'background 0.15s ease'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'rgba(0, 245, 212, 0.12)',
                  border: '1px solid rgba(0, 245, 212, 0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--accent-cyan, #00f5d4)',
                  flexShrink: 0
                }}
              >
                <Receipt size={16} />
              </div>
              <div>
                <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main, #f8fafc)', display: 'block' }}>
                  Non-group expenses
                </span>
                <span style={{ fontSize: '10.5px', color: 'var(--text-muted, #94a3b8)' }}>
                  Direct 1:1 balance outside groups
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              <span
                style={{
                  fontSize: '14px',
                  fontWeight: '700',
                  color: nonGroupAmount > 0 ? 'var(--color-inflow, #00f5d4)' : 'var(--color-outflow, #ff4757)'
                }}
              >
                {nonGroupAmount > 0
                  ? `+${fmt(nonGroupAmount, selectedCurrency)}`
                  : `-${fmt(Math.abs(nonGroupAmount), selectedCurrency)}`}
              </span>
              <ChevronRight size={16} color="var(--text-muted, #94a3b8)" />
            </div>
          </div>
        )}

        {/* More options button */}
        <div
          role="button"
          tabIndex={0}
          onClick={() =>
            onSelectOption({
              type: 'more_options',
              currency: selectedCurrency,
              amount: Math.abs(netAmount),
              direction: netAmount < 0 ? 'pay' : 'receive'
            })
          }
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelectOption({
                type: 'more_options',
                currency: selectedCurrency,
                amount: Math.abs(netAmount),
                direction: netAmount < 0 ? 'pay' : 'receive'
              });
            }
          }}
          style={{
            background: 'rgba(255, 255, 255, 0.02)',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            borderRadius: '12px',
            padding: '12px 14px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            transition: 'background 0.15s ease'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.05)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-muted, #94a3b8)',
                flexShrink: 0
              }}
            >
              <SlidersHorizontal size={16} />
            </div>
            <div>
              <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main, #f8fafc)', display: 'block' }}>
                More options
              </span>
              <span style={{ fontSize: '11px', color: 'var(--text-muted, #94a3b8)' }}>
                Enter custom amount, choose funding account, or date
              </span>
            </div>
          </div>
          <ChevronRight size={16} color="var(--text-muted, #94a3b8)" />
        </div>
      </div>
    </div>
  );
}
